import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TimelineModule } from 'primeng/timeline';
import { ToastModule } from 'primeng/toast';
import { Company } from '../../../company/company.model';
import { Customer } from '../../../customer/customer.model';
import { City } from '../../../models/common/city.model';
import { Country } from '../../../models/common/country.model';
import { Party } from '../../../party/party.model';
import { PartyService } from '../../../party/party.service';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { gccCrNumberValidator, gccRegistrationError, gccVatNumberValidator } from '../../crm-gcc-registration.validation';
import { CRM_REGISTRATION_ATTACHMENT_TYPES, CrmActivityItem, CrmActivityType, CrmClientRegistrationAttachment, CrmCommunicationLog, CrmLeadConversionRequest, CrmNote, CrmProspect, CrmReminder, CrmRoleOption, crmRoleLabel } from '../../crm.model';
import { CrmService } from '../../crm.service';

@Component({
  selector: 'app-prospect-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    BreadcrumbModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    MultiSelectModule,
    TextareaModule,
    ProgressSpinnerModule,
    ToastModule,
    DatePickerModule,
    RadioButtonModule,
    SelectModule,
    TableModule,
    TabsModule,
    TimelineModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent
  ],
  providers: [MessageService],
  templateUrl: './prospect-details.component.html',
  styleUrls: ['./prospect-details.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProspectDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private partyService = inject(PartyService);
  private baseMasterDataService = inject(BaseMasterDataService);
  private sessionStorageService = inject(SessionStorageService);
  private firebaseStorageService = inject(FirebaseStorageService);
  public router = inject(Router);

  prospect = signal<CrmProspect | null>(null);
  notes = signal<CrmNote[]>([]);
  communications = signal<CrmCommunicationLog[]>([]);
  reminders = signal<CrmReminder[]>([]);
  activities = signal<CrmActivityItem[]>([]);
  registrationAttachments = signal<CrmClientRegistrationAttachment[]>([]);
  countries = signal<Country[]>([]);
  cities = signal<City[]>([]);
  crmRoles = signal<CrmRoleOption[]>([]);
  private loadedCityCountryIds = new Set<number>();
  private loadingCityCountryIds = new Set<number>();
  showAttachmentDialog = signal(false);
  uploadingAttachment = signal(false);
  attachmentDraft = signal<Partial<CrmClientRegistrationAttachment>>({ category: 'VAT_CERTIFICATE' });
  attachmentTypes = CRM_REGISTRATION_ATTACHMENT_TYPES;
  conversionDialog = signal(false);
  loadingConversion = signal(false);
  companyOptions = signal<{ label: string; value: number; party: Party }[]>([]);
  employeeOptions = signal<{ label: string; value: number; party: Party }[]>([]);
  showNoteDialog = signal(false);
  newNoteText = signal('');
  showReminderDialog = signal(false);
  newReminder = signal<CrmReminder>({
    dueDate: new Date(),
    reminderText: '',
    completed: false,
    assignedUserId: 0,
    companyId: 0
  });
  showCommDialog = signal(false);
  newCommLog = signal<CrmCommunicationLog>({
    type: 'CALL',
    communicationDate: new Date(),
    summary: '',
    status: 'COMPLETED',
    companyId: 0
  });
  breadcrumbItems = signal<MenuItem[]>([
    { label: 'CRM', routerLink: '/crm/prospects' },
    { label: 'Prospects', routerLink: '/crm/prospects' },
    { label: 'Prospect Details' }
  ]);
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  activeTabValue = 0;
  private readonly tabKeys = ['activity', 'notes', 'reminders', 'attachments'] as const;
  metrics = computed<PageMetric[]>(() => {
    const activeProspect = this.prospect();
    return [
      { label: 'Status', value: activeProspect?.status || 'NEW', tone: 'info' },
      { label: 'Quality', value: activeProspect?.prospectQuality || 'MEDIUM' },
      { label: 'Business Type', value: activeProspect?.businessType || '-' },
      { label: 'Activities', value: this.activities().length, tone: 'success' }
    ];
  });

  businessTypes = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' }
  ];

  conversionForm = this.fb.group({
    existingCompanyPartyId: [null as number | null],
    companyName: ['', Validators.required],
    customerNameLocal: ['', Validators.required],
    postingDate: [new Date() as Date | string | null, Validators.required],
    currencyCode: ['SAR', Validators.required],
    exchangeRate: [1, Validators.required],
    businessType: ['B2B', Validators.required],
    natureOfBusiness: [''],
    crNumber: [null as number | null, [gccCrNumberValidator(() => this.selectedConversionCountry())]],
    taxRegNumber: ['', [gccVatNumberValidator(() => this.selectedConversionCountry())]],
    phoneNumber: [''],
    emailAddress: [''],
    websiteURL: [''],
    address: [''],
    postalCode: [''],
    countryId: [null as number | null, Validators.required],
    cityId: [null as number | null],
    contactName: ['', Validators.required],
    contactEmail: ['', [Validators.email]],
    contactPhoneNumber: [''],
    kamPartyId: [null as number | null],
    clientCategory: ['STANDARD'],
    roles: [[] as string[], Validators.required]
  }, { validators: [this.conversionContactMethodValidator] });

  ngOnInit() {
    const id = Number(this.route.snapshot.params['id']);
    this.route.queryParamMap.subscribe(queryParams => {
      this.activeTabValue = this.resolveTabValue(queryParams.get('tab'));
    });
    this.loadCountries();
    this.loadRoles();
    this.conversionForm.get('countryId')?.valueChanges.subscribe(countryId => {
      this.conversionForm.get('crNumber')?.updateValueAndValidity({ emitEvent: false });
      this.conversionForm.get('taxRegNumber')?.updateValueAndValidity({ emitEvent: false });
      const currentCityId = this.conversionForm.get('cityId')?.value;
      if (!countryId || (currentCityId && !this.cityBelongsToCountry(currentCityId, countryId))) {
        this.conversionForm.patchValue({ cityId: null }, { emitEvent: false });
      }
      this.loadCitiesForCountry(countryId);
    });
    if (id) {
      this.loadProspect(id);
      this.loadTabData(id);
      this.loadConversionLookups();
    }
  }

  onTabChange(value: string | number) {
    this.activeTabValue = Number(value);
    const tabKey = this.tabKeys[this.activeTabValue];
    if (!tabKey) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabKey },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private resolveTabValue(tabKey: string | null): number {
    if (!tabKey) {
      return 0;
    }
    const tabIndex = this.tabKeys.findIndex(key => key === tabKey.toLowerCase());
    return tabIndex >= 0 ? tabIndex : 0;
  }

  loadProspect(id: number) {
    this.crmService.getProspect(id).subscribe(response => {
      if (response.success && response.data) {
        this.prospect.set(response.data);
        if (this.route.snapshot.queryParamMap.get('action') === 'convert-to-client') {
          if (response.data.convertedClientId) {
            this.returnAfterClientConversion(response.data.convertedClientId);
          } else {
            queueMicrotask(() => this.openConvertDialog());
          }
        }
        this.breadcrumbItems.set([
          { label: 'CRM', routerLink: '/crm/prospects' },
          { label: 'Prospects', routerLink: '/crm/prospects' },
          { label: response.data.prospectNumber || 'Prospect Details' }
        ]);
      }
    });
  }

  loadTabData(id: number) {
    this.crmService.getProspectActivityTimeline(id).subscribe(data => this.activities.set(data));
    this.crmService.getNotesByProspect(id).subscribe(data => this.notes.set(data));
    this.crmService.getCommunicationLogsByProspect(id).subscribe(data => this.communications.set(data));
    this.crmService.getRemindersByProspect(id).subscribe(data => this.reminders.set(data));
    this.crmService.getProspectRegistrationAttachments(id).subscribe(data => this.registrationAttachments.set(data || []));
  }

  loadCountries() {
    const cachedCountries = this.sessionStorageService.getCountryList() ?? [];
    if (cachedCountries.length) {
      this.countries.set(cachedCountries);
    }
    this.baseMasterDataService.getCountries('en-US').subscribe({
      next: countries => {
        if (countries?.length) this.countries.set(countries);
      }
    });
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadConversionLookups() {
    const companyId = this.toolbarService.customer()?.company?.id;
    if (!companyId) return;

    this.partyService.getParties(companyId, ['Company'], '', 0, 500).subscribe(page => {
      this.companyOptions.set((page.content || []).map(party => ({
        label: party.entityName,
        value: party.id.entityId,
        party
      })));
    });

    this.partyService.getParties(companyId, ['Employee'], '', 0, 500).subscribe(page => {
      this.employeeOptions.set((page.content || []).map(party => ({
        label: party.entityName,
        value: party.id.entityId,
        party
      })));
    });
  }

  openConvertDialog() {
    const prospect = this.prospect();
    if (!prospect) return;
    this.conversionForm.reset({
      existingCompanyPartyId: null,
      companyName: prospect.companyName,
      customerNameLocal: prospect.customerNameLocal || '',
      postingDate: new Date(),
      currencyCode: 'SAR',
      exchangeRate: 1,
      businessType: prospect.businessType || 'B2B',
      natureOfBusiness: prospect.natureOfBusiness || '',
      crNumber: prospect.crNumber ?? null,
      taxRegNumber: prospect.taxRegNumber || '',
      phoneNumber: prospect.pocPhoneNumber || prospect.mobileNumber || '',
      emailAddress: prospect.pocEmail || '',
      websiteURL: prospect.websiteURL || '',
      address: this.buildProspectAddress(prospect),
      postalCode: prospect.postalCode || '',
      countryId: prospect.countryId ?? this.toolbarService.customer()?.company?.countryId ?? null,
      cityId: null,
      contactName: prospect.pocName,
      contactEmail: prospect.pocEmail,
      contactPhoneNumber: prospect.mobileNumber || prospect.pocPhoneNumber || '',
      kamPartyId: prospect.kamCustomerId ?? null,
      clientCategory: 'STANDARD',
      roles: prospect.roles || []
    }, { emitEvent: false });
    this.conversionForm.get('crNumber')?.updateValueAndValidity({ emitEvent: false });
    this.conversionForm.get('taxRegNumber')?.updateValueAndValidity({ emitEvent: false });
    this.loadCitiesForCountry(this.conversionForm.get('countryId')?.value, () => this.patchConversionCityFromName(prospect.city));
    this.conversionDialog.set(true);
  }

  onExistingCompanyChange(companyId: number | null) {
    const selected = this.companyOptions().find(option => option.value === companyId)?.party;
    if (!selected) return;

    this.conversionForm.patchValue({
      companyName: selected.entityName,
      customerNameLocal: '',
      crNumber: selected.crNumber ? Number(selected.crNumber) : null,
      taxRegNumber: selected.taxRegNumber ?? '',
      phoneNumber: selected.phoneNumber ?? '',
      emailAddress: selected.emailAddress ?? '',
      websiteURL: '',
      address: selected.address ?? '',
      countryId: this.toolbarService.customer()?.company?.countryId ?? null,
      cityId: null
    });
  }

  convertProspectToClient() {
    if (this.loadingConversion()) {
      return;
    }
    const prospect = this.prospect();
    if (!prospect?.id) return;
    const form = this.conversionForm.value;
    if (this.conversionForm.invalid) {
      this.conversionForm.markAllAsTouched();
      const detail = this.conversionValidationMessage();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail });
      return;
    }

    const address = this.buildConversionAddress(form.address ?? '', form.postalCode ?? '', form.cityId ?? null);
    const company = {
      id: form.existingCompanyPartyId ?? undefined,
      name: form.companyName,
      crNumber: form.crNumber ?? null,
      taxRegNumber: form.taxRegNumber ?? null,
      phoneNumber: form.phoneNumber ?? null,
      emailAddress: form.emailAddress ?? null,
      websiteURL: form.websiteURL ?? null,
      address,
      countryId: form.countryId,
      location: undefined as any,
      logo: null,
      placeId: null,
      poBox: null,
      defaultContactId: null,
      transportationAgencyId: null,
      freightForwarderId: null
    } as any as Company;
    const customer = {
      id: undefined,
      firebaseId: `crm-${Date.now()}`,
      name: form.contactName,
      emailAddress: form.contactEmail,
      phoneNumber: form.contactPhoneNumber ?? '',
      imageUrl: null,
      dateOfBirth: undefined,
      gender: undefined,
      totalRating: undefined,
      isPhoneVerified: true,
      corporateRole: 'CONTACT',
      address,
      company,
      countryId: form.countryId
    } as any as Customer;
    const payload: CrmLeadConversionRequest = {
      company,
      customer,
      reuseCompany: !!form.existingCompanyPartyId,
      reuseCustomer: true,
      kamPartyId: form.kamPartyId ?? null,
      clientCategory: form.clientCategory ?? 'STANDARD',
      customerNameLocal: form.customerNameLocal ?? null,
      postingDate: form.postingDate ?? null,
      currencyCode: form.currencyCode ?? null,
      exchangeRate: form.exchangeRate ?? null,
      businessType: form.businessType ?? null,
      natureOfBusiness: form.natureOfBusiness ?? null,
      roles: form.roles || []
    };
    this.loadingConversion.set(true);
    this.crmService.convertProspectToClient(prospect.id, payload).subscribe({
      next: response => {
        this.loadingConversion.set(false);
        if (response.success && response.data?.client?.id) {
          this.prospect.set(response.data.prospect);
          this.conversionDialog.set(false);
          this.messageService.add({ severity: 'success', summary: 'Converted', detail: 'Prospect converted to client' });
          if (!this.returnAfterClientConversion(response.data.client.id)) {
            this.router.navigate(['/crm/clients', response.data.client.id]);
          }
        }
      },
      error: error => {
        this.loadingConversion.set(false);
        const detail = error?.error?.message || error?.error?.detail || 'The prospect could not be converted to a client.';
        this.messageService.add({ severity: 'error', summary: 'Conversion Failed', detail });
      }
    });
  }

  private returnAfterClientConversion(clientId?: number) {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (!returnUrl) return false;
    const separator = returnUrl.includes('?') ? '&' : '?';
    this.router.navigateByUrl(`${returnUrl}${separator}convertedClientId=${clientId ?? ''}`);
    return true;
  }

  saveNote() {
    const activeProspect = this.prospect();
    if (!activeProspect) return;

    const note: CrmNote = {
      prospectId: activeProspect.id,
      text: this.newNoteText(),
      companyId: activeProspect.companyId,
      createdByUserId: this.toolbarService.customer()?.id || 0
    };

    this.crmService.saveNote(note).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Note added' });
        this.showNoteDialog.set(false);
        this.newNoteText.set('');
        this.loadTabData(activeProspect.id!);
      }
    });
  }

  saveReminder() {
    const activeProspect = this.prospect();
    if (!activeProspect) return;

    const reminder = { ...this.newReminder(), prospectId: activeProspect.id, companyId: activeProspect.companyId };
    reminder.assignedUserId = this.toolbarService.customer()?.id || 0;

    this.crmService.saveReminder(reminder).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Reminder set' });
        this.showReminderDialog.set(false);
        this.loadTabData(activeProspect.id!);
      }
    });
  }

  saveCommLog() {
    const activeProspect = this.prospect();
    if (!activeProspect) return;

    const log = { ...this.newCommLog(), prospectId: activeProspect.id, companyId: activeProspect.companyId };
    log.createdByUserId = this.toolbarService.customer()?.id || 0;

    this.crmService.saveCommunicationLog(log).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Activity logged' });
        this.showCommDialog.set(false);
        this.loadTabData(activeProspect.id!);
      }
    });
  }

  createQuotation() {
    const activeProspect = this.prospect();
    if (!activeProspect?.id) return;
    this.router.navigate(['/crm/quotations/new'], {
      queryParams: {
        sourceType: 'PROSPECT',
        sourceId: activeProspect.id
      }
    });
  }

  uploadRegistrationAttachment(event: Event) {
    const activeProspect = this.prospect();
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!activeProspect?.id || !file) return;

    this.uploadingAttachment.set(true);
    this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `crm/prospect-registration/${activeProspect.id}/${Date.now()}-${file.name}`).subscribe({
      next: fileUrl => {
        const attachment: CrmClientRegistrationAttachment = {
          category: this.attachmentDraft().category || 'VAT_CERTIFICATE',
          fileName: file.name,
          fileUrl,
          fileType: file.type,
          fileSize: file.size,
          uploadedByCustomerId: this.toolbarService.customer()?.id
        };
        this.crmService.saveProspectRegistrationAttachment(activeProspect.id!, attachment, activeProspect.companyId).subscribe(response => {
          this.uploadingAttachment.set(false);
          input.value = '';
          if (response.success) {
            this.showAttachmentDialog.set(false);
            this.attachmentDraft.set({ category: 'VAT_CERTIFICATE' });
            this.loadTabData(activeProspect.id!);
          }
        });
      },
      error: () => {
        this.uploadingAttachment.set(false);
        input.value = '';
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload registration attachment.' });
      }
    });
  }

  deleteRegistrationAttachment(attachment: CrmClientRegistrationAttachment) {
    const activeProspect = this.prospect();
    if (!activeProspect?.id || !attachment.id) return;
    this.crmService.deleteProspectRegistrationAttachment(activeProspect.id, attachment.id, activeProspect.companyId).subscribe(() => this.loadTabData(activeProspect.id!));
  }

  registrationAttachmentLabel(category?: string): string {
    return this.attachmentTypes.find(type => type.value === category)?.label || category || '-';
  }

  updateAttachmentCategory(category: CrmClientRegistrationAttachment['category']) {
    this.attachmentDraft.update(value => ({ ...value, category }));
  }

  updateCommType(type: string) {
    this.newCommLog.update(val => ({ ...val, type }));
  }

  updateCommSummary(summary: string) {
    this.newCommLog.update(val => ({ ...val, summary }));
  }

  updateCommDate(date: Date | null) {
    if (date) this.newCommLog.update(val => ({ ...val, communicationDate: date }));
  }

  updateReminderText(text: string) {
    this.newReminder.update(val => ({ ...val, reminderText: text }));
  }

  updateReminderDate(date: Date | null) {
    if (date) this.newReminder.update(val => ({ ...val, dueDate: date }));
  }

  getSeverity(status: string) {
    switch (status) {
      case 'QUALIFIED': return 'success';
      case 'PROPOSAL': return 'warn';
      case 'LOST': return 'danger';
      default: return 'info';
    }
  }

  getActivitySeverity(activityType: CrmActivityType) {
    switch (activityType) {
      case CrmActivityType.NOTE:
        return 'bg-blue-500';
      case CrmActivityType.REMINDER:
        return 'bg-orange-500';
      case CrmActivityType.COMMUNICATION:
      default:
        return 'bg-purple-500';
    }
  }

  getActivityIcon(activityType: CrmActivityType) {
    switch (activityType) {
      case CrmActivityType.NOTE:
        return 'pi pi-pencil';
      case CrmActivityType.REMINDER:
        return 'pi pi-calendar';
      case CrmActivityType.COMMUNICATION:
      default:
        return 'pi pi-comments';
    }
  }

  private buildProspectAddress(prospect: CrmProspect): string {
    return [prospect.street1, prospect.city, prospect.postalCode]
      .filter(part => !!part && String(part).trim().length > 0)
      .join(', ');
  }

  conversionCityOptions() {
    const countryId = this.conversionForm.get('countryId')?.value;
    if (!countryId) return [];
    return this.cities().filter(city => city.country?.id === countryId);
  }

  cityLabel(city?: City | number | null): string {
    if (typeof city === 'number') {
      city = this.cityById(city);
    }
    return city ? [city.cityName, city.country?.name].filter(Boolean).join(', ') : '';
  }

  private loadCitiesForCountry(countryId?: number | null, afterLoad?: () => void) {
    if (!countryId) {
      afterLoad?.();
      return;
    }
    if (this.loadedCityCountryIds.has(countryId)) {
      afterLoad?.();
      return;
    }
    if (this.loadingCityCountryIds.has(countryId)) return;

    this.loadingCityCountryIds.add(countryId);
    this.baseMasterDataService.getCities('en-US', countryId).subscribe({
      next: cities => {
        const existingById = new Map(this.cities().map(city => [city.id, city]));
        (cities ?? []).forEach(city => existingById.set(city.id, city));
        this.cities.set(Array.from(existingById.values()).sort((a, b) => (a.cityName || '').localeCompare(b.cityName || '')));
        this.loadedCityCountryIds.add(countryId);
        this.loadingCityCountryIds.delete(countryId);
        afterLoad?.();
      },
      error: () => {
        this.loadingCityCountryIds.delete(countryId);
        afterLoad?.();
      }
    });
  }

  private cityById(cityId?: number | null) {
    return this.cities().find(city => city.id === cityId);
  }

  private cityBelongsToCountry(cityId?: number | null, countryId?: number | null) {
    if (!cityId || !countryId) return false;
    return this.cityById(cityId)?.country?.id === countryId;
  }

  private patchConversionCityFromName(cityName?: string | null) {
    const normalizedCity = String(cityName || '').trim().toLowerCase();
    if (!normalizedCity || this.conversionForm.get('cityId')?.value) return;
    const matchedCity = this.conversionCityOptions().find(city => {
      const label = this.cityLabel(city).toLowerCase();
      const name = (city.cityName || '').trim().toLowerCase();
      return normalizedCity === name || normalizedCity === label || normalizedCity.startsWith(`${name},`);
    });
    if (matchedCity) {
      this.conversionForm.patchValue({ cityId: matchedCity.id }, { emitEvent: false });
    }
  }

  private buildConversionAddress(address: string, postalCode: string, cityId?: number | null): string {
    const city = this.cityById(cityId);
    const parts = [address, city?.cityName, postalCode]
      .map(part => String(part || '').trim())
      .filter(Boolean);
    const uniqueParts = parts.filter((part, index) => {
      const lowerPart = part.toLowerCase();
      return parts.findIndex(item => item.toLowerCase() === lowerPart || item.toLowerCase().includes(lowerPart)) === index;
    });
    return uniqueParts.join(', ');
  }

  private selectedConversionCountry(): Country | null {
    const countryId = this.conversionForm?.get('countryId')?.value;
    return this.countries().find(country => country.id === countryId) ?? null;
  }

  fieldError(controlName: string): string | null {
    const control = this.conversionForm.get(controlName);
    if (!control || (!control.touched && !control.dirty)) return null;
    if (control.hasError('required')) return controlName === 'roles' ? 'Select at least one business role.' : 'This field is required.';
    if (control.hasError('email')) return 'Enter a valid email address.';
    return gccRegistrationError(control);
  }

  private conversionValidationMessage(): string {
    if (this.conversionForm.hasError('contactMethod')) return 'Contact email or phone is required.';
    for (const controlName of ['companyName', 'customerNameLocal', 'postingDate', 'currencyCode', 'exchangeRate', 'businessType', 'countryId', 'contactName', 'contactEmail', 'crNumber', 'taxRegNumber', 'roles']) {
      const error = this.fieldError(controlName);
      if (error) return error;
    }
    return 'There are validation errors. Please review the highlighted fields.';
  }

  private conversionContactMethodValidator(control: AbstractControl): ValidationErrors | null {
    const email = String(control.get('contactEmail')?.value || '').trim();
    const phone = String(control.get('contactPhoneNumber')?.value || '').trim();
    return email || phone ? null : { contactMethod: true };
  }

  roleLabel(role?: string | null): string {
    return crmRoleLabel(role);
  }
}
